/*
 * `?worker&url` rather than `new URL(..., import.meta.url)`: the plain form
 * ships the worklet to the browser as the TypeScript it was written in, under a
 * media type no browser will run. This one hands over a built, self-contained
 * script at an address of its own, which is what `addModule` needs.
 */
import captureWorkletUrl from './realtimeVoice.capture.worklet.ts?worker&url';
import type { RealtimeCaptureMessage } from './realtimeAudioMessages.ts';

/** PCM16 mono at the one rate our voice sockets and AssemblyAI both speak. */
export const PCM_SAMPLE_RATE = 24_000;

/**
 * The microphone worklet on `context`, handing over 20 ms PCM chunks.
 *
 * Silenced rather than left hanging: the graph is pulled from the speaker, and
 * a node nothing listens to is never asked for a block at all.
 */
export async function createPcmCapture(
  context: AudioContext,
  onChunk: (message: RealtimeCaptureMessage) => void,
): Promise<AudioWorkletNode> {
  await context.audioWorklet.addModule(captureWorkletUrl);

  const capture = new AudioWorkletNode(context, 'realtime-capture', {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: 1,
    outputChannelCount: [1],
  });
  capture.port.onmessage = (event: MessageEvent<RealtimeCaptureMessage>) => {
    onChunk(event.data);
  };

  const silence = context.createGain();
  silence.gain.value = 0;
  capture.connect(silence).connect(context.destination);

  return capture;
}
